import React from 'react'
import { createPortal } from 'react-dom'
import Alert from '@mui/material/Alert'

import { useConnectionBannerVisible } from '../hooks/useConnectionBannerVisible'
import { useStatusBannerHost } from '../hooks/useStatusBannerHost'
import { bannerFlexOrder } from '../utils/statusBannerStack'
import { statusBannerAlertSx } from './styles/statusBanner.styles'

/**
 * Shows a top-centre banner when the user is in a room but the connection is offline.
 * Dismisses automatically when the connection is restored or the user leaves the room.
 *
 * Renders through a portal into `StatusBannerViewport` rather than positioning itself. It was a
 * `Snackbar`, which insists on being `position: fixed` in the layout viewport — under WebKit
 * magnification that put it at a different size and position from the zoom notice, which is
 * pinned to the visible viewport. Sharing one column is what makes the group read as one family.
 *
 * Middle slot: below the audio-resume prompt when that is up, above the SC-255 zoom notice.
 */
const ConnectionUnstableToast: React.FC = () => {
  const show = useConnectionBannerVisible()
  const host = useStatusBannerHost()

  if (!show || !host) return null

  return createPortal(
    <Alert
      severity="warning"
      variant="filled"
      role="status"
      aria-live="polite"
      sx={{
        ...statusBannerAlertSx,
        order: bannerFlexOrder('connection'),
        maxWidth: 'min(560px, calc(100% - 32px))',
      }}
    >
      Connection unstable. You are offline. Reconnecting...
    </Alert>,
    host,
  )
}

export default ConnectionUnstableToast
